"use client";

import { InfoIcon } from "lucide-react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import type {
	CSSProperties,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
} from "react";
import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib";
import {
	QuantitySpinner,
	type QuantitySpinnerColorMode,
	type QuantitySpinnerButtonVariant,
	type QuantitySpinnerSize,
} from "../custom/quantity-spinner";
import { Label } from "../label";
import { DialKitSlider } from "../design-system/dialkit/dialkit-slider";
import { FieldShell, type FieldLayoutHint } from "./field-shell";
import { buildControlDescribedBy, fieldMixedId } from "./field-a11y";

export interface NumberFieldChromeProps {
	showSpinner?: boolean;
	showSlider?: boolean;
	spinnerCenter?: "input" | "display" | "custom";
	spinnerCenterContent?: ReactNode;
	spinnerSize?: QuantitySpinnerSize;
	spinnerButtonVariant?: QuantitySpinnerButtonVariant;
	spinnerColorMode?: QuantitySpinnerColorMode;
	marks?: { value: number; label: string }[];
	step?: number;
	min?: number;
	max?: number;
	/** Clamp commits to this; track may still use `max` for visual range / marks. */
	interactiveMax?: number;
	/** Snap commits to nearest allowed value ≤ interactiveMax. */
	discreteValues?: number[];
	/** When set, replaces tier/value label in slider header or spinner center. */
	valueLabelOverride?: string;
	included?: boolean;
	/** Shown in spinner/input when `included` (default "Included"). */
	includedLabel?: string;
	onResetToIncluded?: () => void;
	resetLabel?: string;
	resetAriaLabel?: string;
	labelInfo?: ReactNode;
	labelInfoAriaLabel?: string;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	/** Min/max mark labels on the same row as the track (pricing parity). */
	inlineRangeLabels?: boolean;
	/** Skip FieldShell wrapper (shop footer, allocation rows, HUD slider). */
	bare?: boolean;
	/** Override spinner ± disabled state (allocation headroom). */
	spinnerDecreaseDisabled?: boolean;
	spinnerIncreaseDisabled?: boolean;
	increaseDisabledTooltip?: string | null;
	/** Formatted idle input display (pricing comma groups). */
	formattedInputValue?: string;
	inputSubtitle?: string;
	/** Commit typed input on blur/Enter instead of each keystroke. */
	inputCommitOnBlur?: boolean;
	/** Controlled input value (shop blur-commit recipe). */
	controlledInputValue?: string;
	onControlledInputChange?: (value: string) => void;
	onControlledInputBlur?: () => void;
	onControlledInputKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
	onControlledInputMouseDown?: (event: MouseEvent<HTMLInputElement>) => void;
	onControlledInputFocus?: () => void;
	inputReadOnly?: boolean;
	sliderClassName?: string;
	sliderTrackClassName?: string;
	sliderRangeClassName?: string;
	sliderThumbClassName?: string;
	sliderTrackStyle?: CSSProperties;
	sliderRangeStyle?: CSSProperties;
	sliderThumbStyle?: CSSProperties;
	sliderPrefix?: ReactNode;
	sliderSuffix?: ReactNode;
	sliderWrapperClassName?: string;
	sliderWrapperRef?: React.RefObject<HTMLDivElement | null>;
	onSliderPointerDown?: (event: React.PointerEvent) => void;
	onSliderPointerDownCapture?: (event: React.PointerEvent) => void;
	onSliderLostPointerCapture?: (event: React.PointerEvent) => void;
	/** When false, slider section omits horizontal inset (HUD compact track). */
	sliderInset?: boolean;
}

export interface NumberFieldProps extends NumberFieldChromeProps {
	id: string;
	label: string;
	value: number;
	onChange: (value: number, source: "spinner" | "slider" | "input") => void;
	/** Called on pointer release after slider drag (when provided). */
	onValueCommit?: (value: number) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	layoutHint?: FieldLayoutHint;
	className?: string;
	decreaseAriaLabel?: string;
	increaseAriaLabel?: string;
	disabled?: boolean;
}

/** Horizontal inset for slider track and tick marks (voice-slider parity). */
const SLIDER_INSET_CLASS = "px-5";

const PRICING_RESET_LINK_CLASS =
	"shrink-0 cursor-pointer text-[10px] font-black uppercase leading-none tracking-[0.12em] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline";

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

function parseQuantityDraft(raw: string): number {
	const trimmed = raw.trim().replace(/,/g, "");
	if (trimmed === "") {
		return Number.NaN;
	}
	return Number(trimmed);
}

function resolveMixedNeutralSliderValue(params: {
	min: number;
	max: number;
	interactiveMax: number;
	discreteValues?: number[];
}): number {
	const { min, interactiveMax, discreteValues } = params;
	if (discreteValues != null && discreteValues.length > 0) {
		const allowed = discreteValues.filter(
			(entry) => entry <= interactiveMax,
		);
		if (allowed.length > 0) {
			return allowed[Math.floor((allowed.length - 1) / 2)] ?? allowed[0];
		}
	}
	return min + (interactiveMax - min) / 2;
}

function snapToNearestDiscreteValue(
	value: number,
	discreteValues: number[],
	interactiveMax: number,
): number {
	const allowed = discreteValues.filter((entry) => entry <= interactiveMax);
	if (allowed.length === 0) {
		return 0;
	}
	let closest = allowed[0];
	for (const candidate of allowed) {
		if (Math.abs(candidate - value) < Math.abs(closest - value)) {
			closest = candidate;
		}
	}
	return closest;
}

function resolveActiveMarkLabel(
	value: number,
	marks: { value: number; label: string }[],
	valueLabelOverride?: string,
): string {
	if (valueLabelOverride != null) {
		return valueLabelOverride;
	}
	if (marks.length === 0) {
		return String(value);
	}
	const sorted = [...marks].sort((a, b) => a.value - b.value);
	let closest = sorted[0];
	for (const mark of sorted) {
		if (
			Math.abs(mark.value - value) < Math.abs(closest.value - value)
		) {
			closest = mark;
		}
	}
	return closest?.label ?? String(value);
}

function resolveInlineRangeLabels(
	min: number,
	max: number,
	marks: { value: number; label: string }[],
): { minLabel: string; maxLabel: string } {
	if (marks.length >= 2) {
		const sorted = [...marks].sort((a, b) => a.value - b.value);
		const first = sorted[0];
		const last = sorted[sorted.length - 1];
		if (first != null && last != null) {
			return { minLabel: first.label, maxLabel: last.label };
		}
	}
	return { minLabel: String(min), maxLabel: String(max) };
}

export function NumberField({
	id,
	label,
	value,
	onChange,
	onValueCommit,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	layoutHint,
	className,
	showSpinner = true,
	showSlider = false,
	spinnerCenter = "display",
	spinnerCenterContent,
	spinnerSize = "default",
	spinnerButtonVariant = "primary",
	spinnerColorMode = "system",
	marks,
	step = 1,
	min = 0,
	max = 100,
	interactiveMax,
	discreteValues,
	valueLabelOverride,
	included = false,
	includedLabel = "Included",
	onResetToIncluded,
	resetLabel = "Reset",
	resetAriaLabel,
	labelInfo,
	labelInfoAriaLabel,
	mixed = false,
	mixedTooltip,
	inlineRangeLabels = false,
	bare = false,
	spinnerDecreaseDisabled,
	spinnerIncreaseDisabled,
	increaseDisabledTooltip = null,
	formattedInputValue,
	inputSubtitle,
	inputCommitOnBlur = false,
	controlledInputValue,
	onControlledInputChange,
	onControlledInputBlur,
	onControlledInputKeyDown,
	onControlledInputMouseDown,
	onControlledInputFocus,
	inputReadOnly,
	sliderClassName,
	sliderTrackClassName,
	sliderRangeClassName,
	sliderThumbClassName,
	sliderTrackStyle,
	sliderRangeStyle,
	sliderThumbStyle,
	sliderPrefix,
	sliderSuffix,
	sliderWrapperClassName,
	sliderWrapperRef,
	onSliderPointerDown,
	onSliderPointerDownCapture,
	onSliderLostPointerCapture,
	sliderInset = true,
	decreaseAriaLabel = "Decrease",
	increaseAriaLabel = "Increase",
	disabled = false,
}: NumberFieldProps) {
	const reactId = useId();
	const mixedId = `${fieldMixedId(id)}-${reactId}`;
	const effectiveInteractiveMax = interactiveMax ?? max;
	const isSliderOnly = showSlider && !showSpinner;
	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description) && !isSliderOnly,
		mixedId: mixed ? mixedId : undefined,
	});
	const isControlledInput = controlledInputValue != null;
	const isPricingInput =
		inputCommitOnBlur &&
		!isControlledInput &&
		spinnerCenter === "input";
	const pendingSelectRef = useRef(false);
	const [draft, setDraft] = useState<string | null>(null);

	const sliderValue = useMemo(() => {
		if (mixed) {
			return resolveMixedNeutralSliderValue({
				min,
				max,
				interactiveMax: effectiveInteractiveMax,
				discreteValues,
			});
		}
		return clamp(value, min, effectiveInteractiveMax);
	}, [
		discreteValues,
		effectiveInteractiveMax,
		max,
		min,
		mixed,
		value,
	]);

	const activeMarkLabel = useMemo(
		() =>
			resolveActiveMarkLabel(
				value,
				marks ?? [],
				valueLabelOverride,
			),
		[marks, value, valueLabelOverride],
	);

	const rangeEdgeLabels = useMemo(
		() => resolveInlineRangeLabels(min, max, marks ?? []),
		[marks, max, min],
	);

	const spinnerDisplayValue =
		valueLabelOverride ??
		(included ? includedLabel : String(sliderValue));

	const showPricingLabelRow =
		labelInfo != null ||
		(onResetToIncluded != null && !included && !disabled);
	const showReset =
		onResetToIncluded != null && !included && !disabled;

	useLayoutEffect(() => {
		if (!isPricingInput || included || !pendingSelectRef.current) {
			return;
		}
		pendingSelectRef.current = false;
		const input = document.getElementById(id) as HTMLInputElement | null;
		if (input == null) {
			return;
		}
		input.focus();
		input.select();
		const frame = requestAnimationFrame(() => {
			input.select();
		});
		return () => cancelAnimationFrame(frame);
	}, [id, included, draft, isPricingInput]);

	function resolveNextValue(rawValue: number): number {
		const capped = clamp(rawValue, min, effectiveInteractiveMax);
		if (discreteValues != null) {
			return snapToNearestDiscreteValue(
				capped,
				discreteValues,
				effectiveInteractiveMax,
			);
		}
		return capped;
	}

	function commit(next: number, source: "spinner" | "slider" | "input") {
		onChange(resolveNextValue(next), source);
	}

	function commitPricingDraft() {
		if (draft == null) {
			return;
		}
		const next = parseQuantityDraft(draft);
		setDraft(null);
		onChange(next, "input");
	}

	function beginEditingAndSelect() {
		pendingSelectRef.current = true;
		setDraft(String(value));
	}

	function handleMarkClick(markValue: number) {
		if (markValue > effectiveInteractiveMax || disabled) {
			return;
		}
		const next = resolveNextValue(markValue);
		onChange(next, "slider");
		onValueCommit?.(next);
	}

	const resolvedDecreaseDisabled =
		spinnerDecreaseDisabled ??
		(disabled || (!included && sliderValue <= min));
	const resolvedIncreaseDisabled =
		spinnerIncreaseDisabled ??
		(disabled || sliderValue >= effectiveInteractiveMax);

	const isEditing = isPricingInput && draft != null;
	const idleFormattedValue =
		formattedInputValue ?? String(sliderValue);
	const inputDisplayValue = included
		? includedLabel
		: isControlledInput
			? controlledInputValue
			: isEditing
				? draft
				: idleFormattedValue;
	const resolvedInputSubtitle =
		included ||
		isEditing ||
		isControlledInput ||
		inputSubtitle == null ||
		inputSubtitle.trim() === ""
			? undefined
			: inputSubtitle;
	const ariaValueNow =
		isControlledInput || isPricingInput
			? (isEditing && Number.isFinite(parseQuantityDraft(draft ?? ""))
					? parseQuantityDraft(draft ?? "")
					: value)
			: sliderValue;
	const idleAriaText =
		resolvedInputSubtitle != null
			? `${idleFormattedValue} ${resolvedInputSubtitle}`
			: idleFormattedValue;
	const displayValueText = included ? includedLabel : idleAriaText;

	// O35.P8 / C18 — showSlider → DialKit Prefer-over (Radix Slider branch deleted).
	// P13.C / C25 — chrome paint props map onto DialKitSlider CSS vars (not dead wrappers).
	const sliderRoot = (
		<div
			className={cn(sliderClassName)}
			onPointerDown={onSliderPointerDown}
			onLostPointerCapture={onSliderLostPointerCapture}
		>
			<DialKitSlider
				label={label}
				hideLabel
				value={sliderValue}
				min={min}
				max={max}
				step={step}
				disabled={disabled}
				trackClassName={sliderTrackClassName}
				rangeClassName={sliderRangeClassName}
				thumbClassName={sliderThumbClassName}
				trackStyle={sliderTrackStyle}
				rangeStyle={sliderRangeStyle}
				thumbStyle={sliderThumbStyle}
				className={cn(
					"relative z-10 min-h-10 py-3",
					mixed && "opacity-80",
				)}
				onChange={(next) => {
					if (isPricingInput) {
						setDraft(null);
					}
					commit(next, "slider");
					onValueCommit?.(resolveNextValue(next));
				}}
			/>
		</div>
	);

	const sliderSection = showSlider ? (
		<div
			data-number-slider
			className={cn(
				"relative flex flex-col gap-1 overflow-visible pt-1",
				isSliderOnly && sliderInset ? SLIDER_INSET_CLASS : "px-1",
				sliderPrefix != null || sliderSuffix != null
					? "pt-0"
					: null,
			)}
		>
			{inlineRangeLabels ? (
				<div className="flex min-w-0 items-center gap-2">
					{sliderPrefix ?? (
						<span
							className="shrink-0 text-[9px] font-medium tabular-nums leading-none text-muted-foreground"
							aria-hidden
						>
							{rangeEdgeLabels.minLabel}
						</span>
					)}
					<div
						ref={sliderWrapperRef}
						className={cn(
							"relative min-w-0 flex-1 overflow-visible",
							sliderWrapperClassName,
						)}
						onPointerDownCapture={onSliderPointerDownCapture}
					>
						{sliderRoot}
					</div>
					{sliderSuffix ?? (
						<span
							className="shrink-0 text-[9px] font-medium tabular-nums leading-none text-muted-foreground"
							aria-hidden
						>
							{rangeEdgeLabels.maxLabel}
						</span>
					)}
				</div>
			) : (
				<div
					ref={sliderWrapperRef}
					className={cn(
						"relative overflow-visible",
						sliderWrapperClassName,
					)}
					onPointerDownCapture={onSliderPointerDownCapture}
				>
					{sliderRoot}
				</div>
			)}
			{marks && marks.length > 0 && !inlineRangeLabels ? (
				<div className="relative z-10 mt-1 min-h-5 w-full overflow-visible">
					{marks.map((mark) => {
						const pct = ((mark.value - min) / (max - min)) * 100;
						const isActive = !mixed && mark.value === value;
						const isDisabledMark =
							mark.value > effectiveInteractiveMax;
						return isSliderOnly ? (
							<button
								key={mark.value}
								type="button"
								disabled={disabled || isDisabledMark}
								className={cn(
									"absolute top-0 -translate-x-1/2 whitespace-nowrap text-[9px] leading-none transition-colors",
									isDisabledMark
										? "cursor-not-allowed text-muted-foreground/40"
										: "cursor-pointer hover:text-foreground",
									isActive && !isDisabledMark
										? "font-medium text-foreground"
										: !isDisabledMark
											? "text-muted-foreground/80"
											: null,
								)}
								style={{ left: `${pct}%` }}
								aria-label={`${label}: ${mark.label}`}
								onClick={() => handleMarkClick(mark.value)}
							>
								{mark.label}
							</button>
						) : (
							<span
								key={mark.value}
								className={cn(
									"absolute -translate-x-1/2 text-[10px] text-muted-foreground",
									isDisabledMark && "text-muted-foreground/40",
								)}
								style={{ left: `${pct}%` }}
							>
								{mark.label}
							</span>
						);
					})}
				</div>
			) : null}
		</div>
	) : null;

	const sliderOnlyHeader = isSliderOnly ? (
		<div className="flex min-h-5 items-center justify-between gap-2">
			<Label htmlFor={id} className="text-xs font-medium">
				{label}
			</Label>
			<span
				className="shrink-0 self-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
				aria-hidden={valueLabelOverride == null ? undefined : true}
			>
				{activeMarkLabel}
			</span>
		</div>
	) : null;

	const sliderOnlyDescription = isSliderOnly && description ? (
		<p className="text-[11px] leading-snug text-muted-foreground">
			{description}
		</p>
	) : null;

	const pricingLabelRow =
		showPricingLabelRow || label ? (
			<div className="flex min-w-0 items-center justify-between gap-2">
				<Label
					htmlFor={id}
					className="inline-flex min-w-0 items-center gap-1 text-xs"
				>
					<span className="min-w-0">{label}</span>
					{labelInfo != null ? (
												<ContentTooltip
							{...CONTENT_TOOLTIP_SHORT}
							side="bottom"
							contentClassName="max-w-xs text-xs leading-snug"
							content={labelInfo}
						>
							<span
																	role="button"
																	tabIndex={0}
																	className="relative z-[1] inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground [transform:translate3d(0,0,0)] [backface-visibility:hidden]"
																	aria-label={
																		labelInfoAriaLabel ?? `About ${label}`
																	}
																	onClick={(event) => {
																		event.preventDefault();
																		event.stopPropagation();
																	}}
																	onPointerDown={(event) => {
																		event.preventDefault();
																		event.stopPropagation();
																	}}
																	onKeyDown={(event) => {
																		if (
																			event.key === "Enter" ||
																			event.key === " "
																		) {
																			event.preventDefault();
																			event.stopPropagation();
																		}
																	}}
																>
																	<InfoIcon
																		className="size-3.5"
																		aria-hidden
																	/>
																</span>
						</ContentTooltip>
					) : null}
				</Label>
				{showReset ? (
					<button
						type="button"
						className={PRICING_RESET_LINK_CLASS}
						aria-label={resetAriaLabel ?? `Reset ${label}`}
						data-pricing-quantity-reset={id}
						onClick={onResetToIncluded}
					>
						{resetLabel}
					</button>
				) : null}
			</div>
		) : null;

	const spinnerSection = showSpinner ? (
		<div
			data-number-spinner
			className={cn(showSpinner && showSlider ? "mb-3" : undefined)}
		>
			{name ? (
				<input type="hidden" name={name} value={String(value)} readOnly />
			) : null}
			<QuantitySpinner
				colorMode={spinnerColorMode}
				decreaseAriaLabel={decreaseAriaLabel}
				increaseAriaLabel={increaseAriaLabel}
				decreaseDisabled={resolvedDecreaseDisabled}
				increaseDisabled={resolvedIncreaseDisabled}
				increaseDisabledTooltip={increaseDisabledTooltip}
				onDecrease={() => {
					if (isPricingInput) {
						setDraft(null);
					}
					commit(sliderValue - step, "spinner");
				}}
				onIncrease={() => {
					if (isPricingInput) {
						setDraft(null);
					}
					commit(sliderValue + step, "spinner");
				}}
				centerMode={
					spinnerCenter === "input" ? "input" : "display"
				}
				size={spinnerSize}
				buttonVariant={spinnerButtonVariant}
				inputId={spinnerCenter === "input" ? id : undefined}
				inputValue={inputDisplayValue}
				inputSubtitle={resolvedInputSubtitle}
				inputReadOnly={
					inputReadOnly ?? (included && !isControlledInput)
				}
				inputDisabled={disabled}
				inputAriaInvalid={Boolean(error)}
				inputAriaDescribedBy={
					spinnerCenter === "input" ? controlDescribedBy : undefined
				}
				onInputMouseDown={
					isControlledInput
						? onControlledInputMouseDown
						: isPricingInput
							? (event) => {
									if (disabled) {
										return;
									}
									event.preventDefault();
									if (included) {
										pendingSelectRef.current = true;
										onChange(value, "input");
										return;
									}
									if (draft == null) {
										beginEditingAndSelect();
									} else {
										pendingSelectRef.current = true;
									}
									const input = event.currentTarget;
									input.focus();
									input.select();
								}
							: undefined
				}
				onInputFocus={
					isControlledInput
						? onControlledInputFocus
						: isPricingInput
							? () => {
									if (included) {
										pendingSelectRef.current = true;
										onChange(value, "input");
										return;
									}
									if (draft == null) {
										beginEditingAndSelect();
										return;
									}
									pendingSelectRef.current = true;
									const input = document.getElementById(
										id,
									) as HTMLInputElement | null;
									input?.select();
									requestAnimationFrame(() => {
										input?.select();
									});
								}
							: undefined
				}
				onInputChange={
					isControlledInput
						? onControlledInputChange
						: isPricingInput && !included
							? (next) => {
									setDraft(next.replace(/,/g, ""));
								}
							: spinnerCenter === "input" && !included
								? (raw) => {
										const parsed = Number(raw);
										if (Number.isFinite(parsed)) {
											commit(parsed, "input");
										}
									}
								: undefined
				}
				onInputBlur={
					isControlledInput
						? () => {
								onControlledInputBlur?.();
								onBlur?.();
							}
						: isPricingInput && !included
							? () => {
									commitPricingDraft();
									onBlur?.();
								}
							: onBlur
				}
				onInputKeyDown={
					isControlledInput
						? onControlledInputKeyDown
						: isPricingInput && !included
							? (event) => {
									if (event.key !== "Enter") {
										return;
									}
									event.preventDefault();
									(event.target as HTMLInputElement).blur();
								}
							: undefined
				}
				inputAriaValueMin={min}
				inputAriaValueMax={effectiveInteractiveMax}
				inputAriaValueNow={included ? undefined : ariaValueNow}
				inputAriaValueText={
					included
						? includedLabel
						: isEditing
							? inputDisplayValue
							: displayValueText
				}
			>
				{spinnerCenter === "custom"
					? spinnerCenterContent
					: spinnerDisplayValue}
			</QuantitySpinner>
		</div>
	) : null;

	const body = (
		<>
			{mixed ? (
				<p id={mixedId} className="mb-2 text-xs text-muted-foreground">
					{mixedTooltip ?? "Mixed values"}
				</p>
			) : null}
			{sliderOnlyHeader}
			{sliderOnlyDescription}
			{showPricingLabelRow || (showSpinner && spinnerCenter === "input") ? (
				<div className="space-y-1.5">
					{pricingLabelRow}
					{spinnerSection}
				</div>
			) : (
				spinnerSection
			)}
			{sliderSection}
			{onResetToIncluded && !included && !showPricingLabelRow ? (
				<button
					type="button"
					className="mt-2 text-xs text-primary underline-offset-2 hover:underline"
					onClick={onResetToIncluded}
					disabled={disabled}
				>
					Reset to included
				</button>
			) : null}
		</>
	);

	if (bare) {
		return (
			<div
				className={cn(
					"flex min-w-0 flex-col gap-2",
					disabled && "pointer-events-none opacity-50",
					className,
				)}
				data-number-field={id}
				data-pricing-included={included ? "true" : undefined}
			>
				{body}
			</div>
		);
	}

	return (
		<FieldShell
			id={id}
			label={
				isSliderOnly || showPricingLabelRow || spinnerCenter === "input"
					? undefined
					: label
			}
			description={isSliderOnly ? undefined : description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(
				disabled && "pointer-events-none opacity-50",
				className,
			)}
			describedById={mixed ? mixedId : undefined}
		>
			<div
				data-number-field={id}
				data-pricing-quantity-control={showPricingLabelRow ? id : undefined}
				data-pricing-included={included ? "true" : undefined}
			>
				{body}
			</div>
		</FieldShell>
	);
}
